import { Platform } from 'react-native';
import { StringUtils } from '~/global/utils/string.utils';

export class EnvironmentVariables {
  constructor(
    private readonly NODE_ENV: string,
    private readonly EXPO_PUBLIC_ZITADEL_AUTHORITY_URL: string,
    private readonly EXPO_PUBLIC_ZITADEL_CLIENT_ID: string,
    private readonly EXPO_PUBLIC_ZITADEL_ORGANIZATION_ID: string,
    private readonly EXPO_PUBLIC_ZITADEL_CALLBACK_URI: string,
    private readonly EXPO_PUBLIC_GRAPHQL_HTTP_URI: string,
    private readonly EXPO_PUBLIC_GRAPHQL_WS_URI: string,
    private readonly EXPO_PUBLIC_WEBSITE_URL: string,
    private readonly EXPO_PUBLIC_POWERSYNC_URL: string,
    private readonly EXPO_PUBLIC_LOG_LEVEL?: string,
    private readonly EXPO_PUBLIC_FLIPT_URL?: string,
    private readonly EXPO_PUBLIC_FLIPT_NAMESPACE?: string,
    private readonly EXPO_PUBLIC_FLIPT_ENVIRONMENT?: string,
    private readonly EXPO_PUBLIC_FLIPT_CLIENT_TOKEN?: string,
    private readonly EXPO_PUBLIC_REVENUECAT_IOS_API_KEY?: string,
    private readonly EXPO_PUBLIC_REVENUECAT_ANDROID_API_KEY?: string,
    private readonly EXPO_PUBLIC_REVENUECAT_TEST_STORE_API_KEY?: string,
    private readonly EXPO_PUBLIC_REVENUECAT_WEB_API_KEY?: string,
    private readonly EXPO_PUBLIC_REVENUECAT_WEB_SANDBOX_API_KEY?: string,
    private readonly EXPO_PUBLIC_REVENUECAT_TEST_MODE?: string,
    private readonly EXPO_PUBLIC_E2E_ENABLED?: string,
  ) {}

  public get nodeEnv() {
    return this.NODE_ENV;
  }

  public get zitadelAuthorityUrl() {
    return new URL(this.EXPO_PUBLIC_ZITADEL_AUTHORITY_URL);
  }

  public get zitadelClientId() {
    return this.EXPO_PUBLIC_ZITADEL_CLIENT_ID;
  }

  public get zitadelOrganizationId() {
    return this.EXPO_PUBLIC_ZITADEL_ORGANIZATION_ID;
  }

  public get zitadelCallbackUri() {
    return new URL(this.EXPO_PUBLIC_ZITADEL_CALLBACK_URI);
  }

  public get graphqlHttpUri() {
    return new URL(this.EXPO_PUBLIC_GRAPHQL_HTTP_URI);
  }

  public get graphqlWsUri() {
    return new URL(this.EXPO_PUBLIC_GRAPHQL_WS_URI);
  }

  public get websiteUrl() {
    return new URL(this.EXPO_PUBLIC_WEBSITE_URL);
  }

  public get powersyncUrl() {
    return new URL(this.EXPO_PUBLIC_POWERSYNC_URL);
  }

  public get logLevel() {
    return this.EXPO_PUBLIC_LOG_LEVEL || 'warn';
  }

  public get fliptUrl() {
    return this.EXPO_PUBLIC_FLIPT_URL;
  }

  public get fliptNamespace() {
    return this.EXPO_PUBLIC_FLIPT_NAMESPACE || 'default';
  }

  public get fliptEnvironment() {
    return this.EXPO_PUBLIC_FLIPT_ENVIRONMENT || 'default';
  }

  public get fliptClientToken() {
    return this.EXPO_PUBLIC_FLIPT_CLIENT_TOKEN;
  }

  /**
   * Whether RevenueCat runs in test mode: the Test Store (native) and the Web
   * Billing sandbox (web) instead of the real stores. Defaults to on outside
   * production so local/dev builds never hit real payment rails; override with
   * `EXPO_PUBLIC_REVENUECAT_TEST_MODE=true|false`.
   */
  public get revenueCatTestMode(): boolean {
    if (this.EXPO_PUBLIC_REVENUECAT_TEST_MODE !== undefined) {
      return this.EXPO_PUBLIC_REVENUECAT_TEST_MODE === 'true';
    }
    return this.NODE_ENV !== 'production';
  }

  /**
   * RevenueCat public SDK key for the current native platform. Publishable keys —
   * safe on the client. In test mode all native platforms use the RevenueCat Test
   * Store key; in normal mode iOS/Android use their store keys. `undefined` on web
   * (use `revenueCatWebApiKey`) and when unset, in which case the layer no-ops.
   */
  public get revenueCatApiKey(): string | undefined {
    if (Platform.OS === 'web') {
      return undefined;
    }
    if (this.revenueCatTestMode) {
      return this.EXPO_PUBLIC_REVENUECAT_TEST_STORE_API_KEY;
    }
    return Platform.select({
      ios: this.EXPO_PUBLIC_REVENUECAT_IOS_API_KEY,
      android: this.EXPO_PUBLIC_REVENUECAT_ANDROID_API_KEY,
    });
  }

  /**
   * RevenueCat Web Billing publishable key. Sandbox key in test mode, production
   * key otherwise. `undefined` when unset (the web purchases layer no-ops).
   */
  public get revenueCatWebApiKey(): string | undefined {
    return this.revenueCatTestMode
      ? this.EXPO_PUBLIC_REVENUECAT_WEB_SANDBOX_API_KEY
      : this.EXPO_PUBLIC_REVENUECAT_WEB_API_KEY;
  }

  /**
   * Whether this is an E2E build (`EXPO_PUBLIC_E2E_ENABLED=true`, set only by the `e2e`
   * EAS profile — never staging/production/store). Gates the test-only `app/e2e/write-token`
   * auth-injection route and its in-app SA-key minter (so the minter + its `node-forge`
   * dependency dead-code-eliminate out of non-e2e bundles). Maestro reliably resolves a
   * testID'd control by putting the `testID` on a text-drawing leaf (which survives Fabric
   * view-flattening), not via a `collapsable` toggle here.
   */
  public get e2eEnabled(): boolean {
    return StringUtils.parseBoolean(this.EXPO_PUBLIC_E2E_ENABLED);
  }
}

export const env = new EnvironmentVariables(
  process.env.NODE_ENV!,
  process.env.EXPO_PUBLIC_ZITADEL_AUTHORITY_URL!,
  Platform.select({
    web: process.env.EXPO_PUBLIC_WEB_ZITADEL_CLIENT_ID,
    native: process.env.EXPO_PUBLIC_NATIVE_ZITADEL_CLIENT_ID,
  })!,
  process.env.EXPO_PUBLIC_ZITADEL_ORGANIZATION_ID!,
  process.env.EXPO_PUBLIC_ZITADEL_CALLBACK_URI!,
  process.env.EXPO_PUBLIC_GRAPHQL_HTTP_URI!,
  process.env.EXPO_PUBLIC_GRAPHQL_WS_URI!,
  process.env.EXPO_PUBLIC_WEBSITE_URL!,
  process.env.EXPO_PUBLIC_POWERSYNC_URL!,
  process.env.EXPO_PUBLIC_LOG_LEVEL,
  process.env.EXPO_PUBLIC_FLIPT_URL,
  process.env.EXPO_PUBLIC_FLIPT_NAMESPACE,
  process.env.EXPO_PUBLIC_FLIPT_ENVIRONMENT,
  process.env.EXPO_PUBLIC_FLIPT_CLIENT_TOKEN,
  process.env.EXPO_PUBLIC_REVENUECAT_IOS_API_KEY,
  process.env.EXPO_PUBLIC_REVENUECAT_ANDROID_API_KEY,
  process.env.EXPO_PUBLIC_REVENUECAT_TEST_STORE_API_KEY,
  process.env.EXPO_PUBLIC_REVENUECAT_WEB_API_KEY,
  process.env.EXPO_PUBLIC_REVENUECAT_WEB_SANDBOX_API_KEY,
  process.env.EXPO_PUBLIC_REVENUECAT_TEST_MODE,
  process.env.EXPO_PUBLIC_E2E_ENABLED,
);
